import { Controller, useFormContext, ControllerRenderProps, FieldValues } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { KeyboardEvent, FocusEvent } from "react";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";
import { FieldLabel } from "ui/forms";

import Icons from "assets";

interface ThemeInputNumberProps {
  name: string;
  label: string;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  step?: number;
  onValueChange: (value: string, onChange: (value: string) => void) => void;
  onArrowUp: (
    field: ControllerRenderProps<FieldValues, string>,
    onChange: (value: string) => void,
  ) => void;
  onArrowDown: (
    field: ControllerRenderProps<FieldValues, string>,
    onChange: (value: string) => void,
  ) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>, value: string) => void;
  onBlur?: (
    event: FocusEvent<HTMLInputElement>,
    field: ControllerRenderProps<FieldValues, string>,
  ) => void;
}

const ThemeInputNumber = ({
  name,
  label,
  minValue = 0,
  maxValue = 100,
  defaultValue = 0,
  step = 1,
  onValueChange,
  onArrowUp,
  onArrowDown,
  onKeyDown,
  onBlur,
}: ThemeInputNumberProps) => {
  const { isEditMode } = useThemePreviewContext();
  const { control } = useFormContext();

  const handleBlur = (
    e: FocusEvent<HTMLInputElement>,
    field: ControllerRenderProps<FieldValues, string>,
  ) => {
    if (e.target.value === "") {
      onValueChange(defaultValue.toString(), field.onChange);
    } else if (onBlur) {
      onBlur(e, field);
    }
  };

  const textInputStyles = twMerge(
    "text-base cursor-default focus:outline-none w-full text-neutrals-900 h-10 rounded-l-md",
    "gap-2.5 px-2.5 py-1 items-center",
    !isEditMode && "bg-neutrals-200 text-neutrals-500",
    "hover:ring-1 hover:ring-teal-700 focus:ring-1 focus:ring-teal-700 focus:shadow-input focus:z-10 hover:z-10 outline-none disabled:hover:ring-0",
  );

  const baseArrowStyles = twMerge(
    "text-xs px-1 h-1/2 flex items-center justify-center bg-white disabled:bg-neutrals-100",
    "hover:ring-1 hover:ring-teal-700 focus:ring-1 focus:ring-teal-700 focus:shadow-input focus:z-10 hover:z-10 outline-none disabled:hover:ring-0",
  );

  const arrowTopStyles = twMerge(baseArrowStyles, "border-b rounded-tr-md");
  const arrowBottomStyles = twMerge(baseArrowStyles, "rounded-br-md");

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div>
          <FieldLabel name={name}>{label}</FieldLabel>
          <div className="flex mt-1.5 gap-2 font-normal">
            <div className="relative flex w-full rounded-md border shadow-base">
              <input
                type="number"
                className={textInputStyles}
                disabled={!isEditMode}
                min={minValue}
                max={maxValue}
                step={step}
                value={field.value || ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const cleaned = raw.replace(/^0+(?=\d)/, "");
                  onValueChange(cleaned, field.onChange);
                }}
                onBlur={(e) => handleBlur(e, field)}
                onKeyDown={(e) => onKeyDown?.(e, field.value)}
              />
              <div className="flex flex-col h-10 w-7 rounded-r-md border-l">
                <button
                  type="button"
                  disabled={field.value >= maxValue || !isEditMode}
                  onClick={() => {
                    onArrowUp(field, field.onChange);
                  }}
                  className={arrowTopStyles}>
                  <Icons.ArrowRight className="-rotate-90" />
                </button>
                <button
                  type="button"
                  disabled={field.value <= minValue || !isEditMode}
                  onClick={() => {
                    onArrowDown(field, field.onChange);
                  }}
                  className={arrowBottomStyles}>
                  <Icons.ArrowRight className="rotate-90" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    />
  );
};

export default ThemeInputNumber;
